import { useId, useState } from 'react';
import { MIN_SEGMENT_SIZE, type RaterProgressDto, type RaterRelationship } from '@genoma/shared';

import { useI18n } from '../../i18n/index.tsx';
import { Meter } from '../dashboard/Meter.tsx';

type Props = {
  relationship: RaterRelationship;
  raters: RaterProgressDto[];
};

/**
 * One segment of a participant's 360, openable to see who is in it.
 *
 * P&O sees names and whether each person has answered — that is what chasing
 * somebody requires. It sees nothing about *what* anyone answered: no value,
 * no average, not even how many questions they got through. The payload behind
 * this list carries none of it, so there is nothing here to leak.
 *
 * Closed by default: the numbers answer the usual question, and the names are
 * only needed when somebody is about to send a reminder.
 */
export const RaterGroup = ({ relationship, raters }: Props) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const listId = useId();

  const answered = raters.filter((rater) => rater.hasAnswered).length;
  // Finished is submitted, not "has answered something". A rater on the last
  // screen with every score in has answered and has not finished.
  const submitted = raters.filter((rater) => rater.hasSubmitted).length;
  const isSelf = relationship === 'SELF';
  const isSupervisor = relationship === 'SUPERVISOR';

  return (
    <div className="border-t border-line-soft/70 py-2.5 first:border-t-0 first:pt-0">
      <button
        type="button"
        onClick={() => setOpen((shown) => !shown)}
        aria-expanded={open}
        aria-controls={listId}
        className="group flex w-full cursor-pointer items-center gap-2 border-none bg-transparent p-0 text-left"
      >
        <span className="min-w-0 flex-1">
          <Meter
            label={t(`relationships.${relationship}`)}
            responded={answered}
            submitted={submitted}
            invited={raters.length}
            // Neither the supervisor nor the self-assessment is a pooled
            // segment, so the minimum-size tick would mean nothing on them.
            {...(isSelf || isSupervisor ? {} : { threshold: MIN_SEGMENT_SIZE })}
          />
        </span>
        <span
          aria-hidden
          className={[
            'mt-3 shrink-0 font-mono text-[10px] text-faint transition-transform duration-200 group-hover:text-teal',
            open ? 'rotate-180' : '',
          ].join(' ')}
        >
          ▾
        </span>
      </button>

      {open && (
        <ul id={listId} className="mt-2 list-none rounded-xl bg-sand px-3 py-2">
          {raters.map((rater) => (
            <li
              key={rater.personId}
              className="flex items-center gap-2.5 border-t border-line-soft/60 py-1.5 first:border-t-0"
            >
              {/* Status by shape and label as well as colour, so it never rests
                  on colour alone. */}
              <span
                aria-hidden
                className={[
                  'size-1.5 shrink-0 rounded-full',
                  rater.hasAnswered ? 'bg-teal' : 'bg-line',
                ].join(' ')}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-ink">{rater.fullName}</span>
                {rater.jobTitle && (
                  <span className="block truncate text-[11.5px] text-faint">
                    {rater.jobTitle}
                  </span>
                )}
              </span>
              <span
                className={[
                  'shrink-0 font-mono text-[10px] tracking-[0.06em] uppercase',
                  rater.hasAnswered ? 'text-teal-deep' : 'text-faint',
                ].join(' ')}
              >
                {rater.hasSubmitted
                  ? t('participants.raterSubmitted')
                  : rater.hasAnswered
                    ? t('participants.raterInProgress')
                    : t('participants.raterPending')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
